import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { Button, Empty, Input, Spin } from "antd";
import { IconLineSearch } from "@kalodata/kds-design-icons";
import { ProductOption } from "./ProductOption";
import { toPickerValue, type ProductPickerValue } from "./types";
import type { UseAccountProductsResult } from "./useAccountProducts";

const LOAD_MORE_OFFSET = 32;

interface ReplacePopoverProps {
  list: UseAccountProductsResult;
  currentSelectedId: string;
  onSave: (next: ProductPickerValue) => void;
  onCancel: () => void;
}

export const ReplacePopover = ({ list, currentSelectedId, onSave, onCancel }: ReplacePopoverProps) => {
  const [keyword, setKeyword] = useState("");
  const [tempId, setTempId] = useState(currentSelectedId);

  useEffect(() => {
    setTempId(currentSelectedId);
  }, [currentSelectedId]);

  const filtered = useMemo(() => list.filter(keyword), [list, keyword]);
  const tempProduct = useMemo(() => list.products.find((p) => p.productId === tempId), [list.products, tempId]);
  const canSave = Boolean(tempProduct) && tempId !== currentSelectedId;

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (
        list.hasMore &&
        !list.loadingMore &&
        target.scrollTop + target.clientHeight >= target.scrollHeight - LOAD_MORE_OFFSET
      ) {
        void list.loadMore();
      }
    },
    [list],
  );

  const showInitialSpin = list.loading && list.products.length === 0;

  return (
    <div className="flex w-[480px] max-w-[90vw] flex-col rounded-[8px] bg-kds-bg-card">
      <div className="flex max-h-[320px] min-h-[160px] flex-col gap-[10px] p-[12px]">
        <Input
          allowClear
          autoFocus
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder={t("Search or select a product")}
          prefix={<IconLineSearch size={16} className="text-kds-fg-soft" />}
        />
        <div className="flex min-h-0 flex-1 flex-col gap-[8px] overflow-y-auto" onScroll={handleScroll}>
          {showInitialSpin ? (
            <div className="flex h-[120px] items-center justify-center">
              <Spin size="small" />
            </div>
          ) : filtered.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Trans>No products match your search</Trans>} />
          ) : (
            <>
              {filtered.map((product) => (
                <button
                  key={product.productId}
                  type="button"
                  onClick={() => setTempId(product.productId ?? "")}
                  className="cursor-pointer rounded-[8px] border-0 bg-transparent p-0 text-left transition-colors hover:bg-kds-bg-overlay"
                >
                  <ProductOption product={product} keyword={keyword} selected={product.productId === tempId} />
                </button>
              ))}
              {list.loadingMore ? (
                <div className="flex items-center justify-center py-[8px]">
                  <Spin size="small" />
                </div>
              ) : !list.hasMore && filtered.length > 0 ? (
                <div className="flex items-center justify-center py-[8px] text-[12px] text-kds-fg-soft">
                  <Trans>No more items</Trans>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-start border-t border-kds-border px-[12px] pb-[12px] pt-[8px]">
        <Button
          size="small"
          type="primary"
          disabled={!canSave}
          onClick={() => {
            if (tempProduct) onSave(toPickerValue(tempProduct));
            else onCancel();
          }}
        >
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  );
};
